import { useState, useEffect } from 'react';
import { API_BASE } from '../config';

const API = `${API_BASE.replace(/\/api$/, '')}/api/intel`;

const authFetch = (url, options = {}) => {
    const token = localStorage.getItem('accessToken')
    return fetch(url, {
        ...options,
        headers: { ...options.headers, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
};

const COUNTRIES = ['All', 'Vietnam', 'Philippines', 'Singapore', 'International'];

// Category tabs with hover effects
function CategoryTabs({ categories, selectedCategory, onCategoryClick }) {
    const [hoveredCat, setHoveredCat] = useState(null);
    return (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', overflowX: 'auto', paddingBottom: '4px' }}>
            {categories.map((cat) => {
                const isSelected = selectedCategory === cat.name;
                const isHovered = hoveredCat === cat.name;
                return (
                    <div
                        key={cat.name}
                        onClick={() => onCategoryClick(cat.name)}
                        onMouseEnter={() => setHoveredCat(cat.name)}
                        onMouseLeave={() => setHoveredCat(null)}
                        style={{
                            background: isSelected ? 'rgba(212,175,55,0.15)' : '#142238',
                            border: isSelected ? '2px solid #d4af37' : (isHovered ? '2px solid #d4af37' : '1px solid #1e3a5f'),
                            borderRadius: '8px', padding: '8px 16px',
                            whiteSpace: 'nowrap', flexShrink: 0,
                            color: isSelected ? '#d4af37' : (isHovered ? '#d4af37' : '#fff'),
                            fontSize: '13px', fontWeight: 600,
                            transition: 'border-color 0.15s, color 0.15s',
                            userSelect: 'none',
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '6px',
                        }}
                    >
                        {cat.name}
                        <span style={{
                            background: isSelected ? 'rgba(212,175,55,0.3)' : 'rgba(255,255,255,0.1)',
                            padding: '1px 8px', borderRadius: '10px', fontSize: '11px',
                        }}>
                            {cat.count}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

export default function TenderIntel() {
    const [tab, setTab] = useState(0);
    const [stats, setStats] = useState(null);
    const [patterns, setPatterns] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [gaps, setGaps] = useState([]);
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(false);
    const [scanJob, setScanJob] = useState(null);
    const [scanStatus, setScanStatus] = useState(null);
    const [vendorPage, setVendorPage] = useState(0);

    // Tender list state
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [tenders, setTenders] = useState([]);
    const [tendersTotal, setTendersTotal] = useState(0);
    const [loadingTenders, setLoadingTenders] = useState(false);
    const [tenderPage, setTenderPage] = useState(0);
    const TENDER_LIMIT = 20;

    // Expanded tender detail
    const [expandedTender, setExpandedTender] = useState(null);

    // Manual search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchImage, setSearchImage] = useState(null);
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState(null);

    // Fetch stats and build category tabs dynamically
    const fetchStats = async () => {
        try {
            const res = await authFetch(`${API}/stats`);
            const data = await res.json();
            setStats(data);

            // Build categories from DB stats
            const cats = [
                { name: 'All', count: data.total_tenders_raw || 0 },
                ...(data.top_categories || []).map(c => ({
                    name: c.category,
                    count: c.count
                }))
            ];
            setCategories(cats);
        } catch (e) {
            console.error('Failed to fetch stats:', e);
        }
    };

    // Fetch tenders with category + country filter
    const fetchTenders = async (category, country, page = 0) => {
        setLoadingTenders(true);
        let url = `${API}/tenders?limit=${TENDER_LIMIT}&offset=${page * TENDER_LIMIT}`;
        if (category && category !== 'All') {
            url += `&category=${encodeURIComponent(category)}`;
        }
        if (country) {
            url += `&country=${encodeURIComponent(country)}`;
        }
        console.log('Fetching tenders from:', url);
        try {
            const res = await authFetch(url);
            const data = await res.json();
            setTenders(data.tenders || []);
            setTendersTotal(data.total || 0);
        } catch (e) {
            console.error('Failed to fetch tenders:', e);
            setTenders([]);
            setTendersTotal(0);
        }
        setLoadingTenders(false);
    };

    const handleCategoryClick = (categoryName) => {
        console.log('Category clicked:', categoryName);
        setSelectedCategory(categoryName);
        setTenderPage(0);
        setExpandedTender(null);
        fetchTenders(categoryName, selectedCountry, 0);
    };

    const handleCountryClick = (countryName) => {
        console.log('Country clicked:', countryName);
        const country = countryName === 'All' ? null : countryName;
        setSelectedCountry(country);
        setTenderPage(0);
        setExpandedTender(null);
        fetchTenders(selectedCategory, country, 0);
    };

    const handleTenderClick = (tender) => {
        setExpandedTender(expandedTender === tender.id ? null : tender.id);
    };

    const getTenderTitle = (tender) => {
        if (tender.title && tender.title.trim() && tender.title !== 'null' && tender.title.length > 0) {
            return tender.title;
        }
        if (tender.tender_title && tender.tender_title.trim() && tender.tender_title !== 'null') {
            return tender.tender_title;
        }
        if (tender.problem_being_solved_vi) {
            return tender.problem_being_solved_vi;
        }
        if (tender.problem_being_solved) {
            return tender.problem_being_solved;
        }
        if (tender.solution_category) {
            return `${tender.solution_category} Tender`;
        }
        return 'LEA Procurement Tender';
    };

    // Fetch all data on mount
    useEffect(() => {
        fetchStats();
        fetchAllTabs();
    }, []);

    // Fetch tenders when category/country/page changes
    useEffect(() => {
        fetchTenders(selectedCategory, selectedCountry, tenderPage);
    }, [selectedCategory, selectedCountry, tenderPage]);

    const fetchAllTabs = async () => {
        setLoading(true);
        try {
            const [pRes, vRes, gRes, srcRes] = await Promise.all([
                authFetch(`${API}/patterns`).then(r => r.json()),
                authFetch(`${API}/vendors`).then(r => r.json()),
                authFetch(`${API}/gaps`).then(r => r.json()),
                authFetch(`${API}/sources`).then(r => r.json()),
            ]);
            setPatterns(pRes?.patterns || []);
            setVendors(vRes?.vendors || []);
            setGaps(gRes?.gaps || []);
            setSources(srcRes?.sources || []);
        } catch (e) {
            console.error('Failed to fetch intel data:', e);
        }
        setLoading(false);
    };

    const handleSearch = async () => {
        if (!searchQuery && !searchImage) return;
        setSearching(true);
        try {
            const formData = new FormData();
            if (searchQuery) formData.append('query', searchQuery);
            if (searchImage) formData.append('image', searchImage);
            const res = await authFetch(`${API}/manual-search`, {
                method: 'POST',
                body: formData,
            });
            const data = await res.json();
            setSearchResults(data);
            const searchRes = await authFetch(`${API}/manual-search/${data.search_id}`);
            const fullData = await searchRes.json();
            setSearchResults(fullData?.results || data);
        } catch (e) {
            console.error('Search failed:', e);
        }
        setSearching(false);
    };

    const startScan = async (extractOnly = false) => {
        setLoading(true);
        setScanStatus(null);
        try {
            const res = await authFetch(`${API}/scan`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ source_ids: [], extract_only: extract_only })
            });
            const data = await res.json();
            const jobId = data.job_id;
            if (extractOnly) {
                setScanStatus({ status: 'processing', message: 'Extracting intelligence...' });
                setTimeout(() => { fetchAllTabs(); fetchStats(); setScanStatus(null); }, 60000);
                return;
            }
            if (!jobId || jobId === 'undefined') return;
            setScanJob(jobId);
            let attempts = 0;
            const poll = setInterval(async () => {
                try {
                    attempts++;
                    const sRes = await authFetch(`${API}/scan/${jobId}`).then(r => r.json());
                    setScanStatus(sRes);
                    if (sRes.status === 'completed' || sRes.status === 'failed' || attempts > 120) {
                        clearInterval(poll);
                        fetchAllTabs();
                        fetchStats();
                    }
                } catch (e) { console.warn('Poll error:', e); }
            }, 3000);
        } catch (e) { console.error('Scan failed:', e); }
        setLoading(false);
    };

    const toggleSource = async (id, current) => {
        try {
            await authFetch(`${API}/sources/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ is_active: !current }) });
            fetchAllTabs();
        } catch (e) { }
    };

    const confColor = (c) => c > 0.7 ? '#22c55e' : c > 0.5 ? '#eab308' : '#ef4444';
    const tabs = ['Patterns', 'Vendors', 'Gaps', 'Sources'];

    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) setSearchImage(file);
    };

    if (loading && !stats) {
        return <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading Tender Intelligence...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h2 style={{ color: '#d4af37', margin: 0 }}>🧠 Tender Intelligence</h2>
                <button onClick={() => { startScan(false); }} disabled={loading} className="btn-primary">
                    🔄 Scan All Sources
                </button>
            </div>

            {/* Scan Status */}
            {scanStatus && (
                <div className="card" style={{ marginBottom: '16px', background: '#142238', padding: '16px' }}>
                    <div style={{ color: '#d4af37', fontWeight: 600, marginBottom: '12px' }}>
                        Scan Job #{scanJob} — {scanStatus.status}
                    </div>
                    <div style={{ background: '#0a1929', borderRadius: '4px', height: '8px', marginBottom: '12px' }}>
                        <div style={{
                            background: '#d4af37', height: '100%', borderRadius: '4px',
                            width: scanStatus.tenders_scraped ? `${(scanStatus.tenders_processed / scanStatus.tenders_scraped) * 100}%` : '0%',
                        }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', fontSize: '13px', color: '#94a3b8' }}>
                        <span>📥 Scraped: {scanStatus.tenders_scraped || 0}</span>
                        <span>🔍 New: {scanStatus.tenders_new || 0}</span>
                        <span>⚙️ Processed: {scanStatus.tenders_processed || 0}</span>
                        <span>🏢 Vendors: {scanStatus.new_vendors_found || 0}</span>
                    </div>
                </div>
            )}

            {/* Stats Row */}
            {stats && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                    {[
                        { label: 'Total Tenders', value: stats.total_tenders_raw, color: '#fff' },
                        { label: 'LEA-Relevant', value: stats.lea_relevant, color: '#d4af37' },
                        { label: 'Processed', value: stats.processed, color: '#fff' },
                        { label: 'Vendors', value: stats.vendors_discovered, color: '#22c55e' },
                        { label: 'Patterns', value: stats.patterns_found, color: '#3b82f6' },
                    ].map((s, i) => (
                        <div key={i} className="card" style={{ background: '#142238', padding: '16px' }}>
                            <div style={{ fontSize: '12px', color: '#94a3b8' }}>{s.label}</div>
                            <div style={{ fontSize: '28px', fontWeight: 700, color: s.color }}>{s.value || 0}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Manual Search Bar */}
            <div className="card" style={{ background: '#142238', padding: '16px', marginBottom: '20px' }}>
                <div style={{ fontSize: '14px', color: '#d4af37', fontWeight: 600, marginBottom: '12px' }}>
                    🔍 Manual Tender Search
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                        type="text"
                        placeholder="Search tenders by keyword..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                        style={{
                            flex: 1, padding: '10px 14px', background: '#0a1929', border: '1px solid rgba(212,175,55,0.3)',
                            borderRadius: '6px', color: '#fff', fontSize: '14px', outline: 'none',
                        }}
                    />
                    <label
                        style={{
                            padding: '10px 14px', background: searchImage ? 'rgba(212,175,55,0.2)' : '#142238',
                            border: '1px solid rgba(212,175,55,0.3)', borderRadius: '6px', cursor: 'pointer',
                            color: '#d4af37', fontSize: '18px', lineHeight: 1,
                        }}
                    >
                        📷
                        <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                    </label>
                    {searchImage && (
                        <span style={{ fontSize: '12px', color: '#94a3b8', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {searchImage.name}
                        </span>
                    )}
                    <button onClick={handleSearch} disabled={searching} className="btn-primary" style={{ padding: '10px 20px' }}>
                        {searching ? '⏳ Searching...' : '🔍 Search'}
                    </button>
                </div>
                {searchResults && (
                    <div style={{ marginTop: '12px', padding: '12px', background: '#0a1929', borderRadius: '6px', fontSize: '13px' }}>
                        <div style={{ color: '#d4af37', fontWeight: 600, marginBottom: '8px' }}>
                            Search: "{searchResults.query || searchResults.results?.query}" — {searchResults?.results?.total_tenders || searchResults.total_tenders || 0} results
                        </div>
                        {searchResults.results?.tender_results && Object.entries(searchResults.results.tender_results).map(([cat, items]) => (
                            <div key={cat} style={{ marginBottom: '8px' }}>
                                <span style={{ color: '#d4af37' }}>{cat}</span>
                                <span style={{ color: '#94a3b8' }}> — {items.length} tenders</span>
                            </div>
                        ))}
                        {searchResults.results?.web_results?.length > 0 && (
                            <div style={{ color: '#94a3b8', marginTop: '8px' }}>
                                {searchResults.results.web_results.length} web results found
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* ===== Category Tabs (dynamic from DB) ===== */}
            <CategoryTabs
                categories={categories}
                selectedCategory={selectedCategory}
                onCategoryClick={handleCategoryClick}
            />

            {/* ===== Country Filter ===== */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                {COUNTRIES.map(country => (
                    <button
                        key={country}
                        onClick={() => handleCountryClick(country)}
                        style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            border: (selectedCountry === null && country === 'All') || selectedCountry === country
                                ? '2px solid #D4AF37'
                                : '1px solid #1e3a5f',
                            background: ((selectedCountry === null && country === 'All') || selectedCountry === country)
                                ? '#D4AF37'
                                : 'transparent',
                            color: ((selectedCountry === null && country === 'All') || selectedCountry === country)
                                ? '#0a1628'
                                : '#D4AF37',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        {country}
                    </button>
                ))}
            </div>

            {/* ===== Tender List ===== */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ color: '#d4af37', fontWeight: 600, fontSize: '16px', marginBottom: '12px' }}>
                    📋 {selectedCategory ? `Tenders — ${selectedCategory}` : 'All Tenders'}
                    {selectedCountry ? ` • ${selectedCountry}` : ''}
                </div>

                {loadingTenders ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading tenders...</div>
                ) : tenders.length === 0 ? (
                    <div className="card" style={{ background: '#142238', padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                        No tenders found{selectedCategory ? ` for ${selectedCategory}` : ''}{selectedCountry ? ` in ${selectedCountry}` : ''}. Run a scan to discover tenders.
                    </div>
                ) : (
                    <>
                        <div style={{ display: 'grid', gap: '10px' }}>
                            {tenders.map((t) => {
                                const isExpanded = expandedTender === t.id;
                                const title = getTenderTitle(t);
                                const vendorsArr = Array.isArray(t.vendors_mentioned) ? t.vendors_mentioned :
                                    Array.isArray(t.typical_vendors) ? t.typical_vendors :
                                        Array.isArray(t.winning_vendor) ? t.winning_vendor :
                                            Array.isArray(t.products_in_solution) ? t.products_in_solution.map(p => typeof p === 'string' ? p : p.vendor || p.name).filter(Boolean) : [];
                                const vendorNames = vendorsArr.slice(0, 2).map(v => typeof v === 'string' ? v : v.name || v.vendor || '').filter(Boolean);

                                return (
                                    <div key={t.id || t.intel_id} style={{ cursor: 'pointer' }}>
                                        <div
                                            className="card"
                                            onClick={() => handleTenderClick(t)}
                                            style={{
                                                background: '#142238', padding: '16px',
                                                borderLeft: isExpanded ? '3px solid #3b82f6' : '3px solid #d4af37',
                                                transition: 'border-color 0.2s',
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ color: '#fff', fontWeight: 600, fontSize: '15px', marginBottom: '6px' }}>
                                                        📋 {title}
                                                    </div>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: '13px', color: '#94a3b8', marginBottom: '8px' }}>
                                                        <span>🏢 {t.issuing_agency || 'Unknown Agency'}</span>
                                                        {t.country && <span>🌍 {t.country}</span>}
                                                        {t.solution_category && <span>📂 {t.solution_category}</span>}
                                                        {t.budget_range && <span style={{ color: '#22c55e' }}>💰 {t.budget_range}</span>}
                                                    </div>
                                                    {vendorNames.length > 0 && (
                                                        <div style={{ fontSize: '12px', color: '#3b82f6', marginBottom: '6px' }}>
                                                            🏭 {vendorNames.join(', ')}
                                                        </div>
                                                    )}
                                                    {t.problem_being_solved && (
                                                        <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: 1.4 }}>
                                                            📝 {t.problem_being_solved.length > 120 ? t.problem_being_solved.slice(0, 120) + '...' : t.problem_being_solved}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    {t.source_link || t.source_url || t.intel_source_link ? (
                                                        <a href={t.source_link || t.source_url || t.intel_source_link} target="_blank" rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ padding: '4px 12px', fontSize: '12px', color: '#d4af37', textDecoration: 'none' }} className="btn-secondary">
                                                            🔗 View Source ↗
                                                        </a>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Expanded detail view */}
                                        {isExpanded && (
                                            <div className="card" style={{ background: '#0a1929', padding: '16px', marginTop: '-6px', borderLeft: '3px solid #3b82f6' }}>
                                                <div style={{ fontSize: '14px', fontWeight: 600, color: '#d4af37', marginBottom: '12px' }}>
                                                    📄 Full Details
                                                </div>

                                                {t.problem_being_solved && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Problem Description:</div>
                                                        <div style={{ fontSize: '13px', color: '#ccc', lineHeight: 1.5 }}>{t.problem_being_solved}</div>
                                                    </div>
                                                )}
                                                {t.problem_being_solved_vi && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Mô tả vấn đề (Tiếng Việt):</div>
                                                        <div style={{ fontSize: '13px', color: '#ccc', lineHeight: 1.5 }}>{t.problem_being_solved_vi}</div>
                                                    </div>
                                                )}

                                                {vendorNames.length > 0 && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Vendors Mentioned:</div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {vendorsArr.map((v, vi) => {
                                                                const name = typeof v === 'string' ? v : v.name || v.vendor || '';
                                                                return name ? (
                                                                    <span key={vi} style={{
                                                                        background: 'rgba(59,130,246,0.15)', color: '#3b82f6',
                                                                        padding: '2px 8px', borderRadius: '12px', fontSize: '12px'
                                                                    }}>
                                                                        {name}
                                                                    </span>
                                                                ) : null;
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {t.products_in_solution && Array.isArray(t.products_in_solution) && t.products_in_solution.length > 0 && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Products in Solution:</div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                            {t.products_in_solution.map((p, pi) => {
                                                                const name = typeof p === 'string' ? p : p.name || '';
                                                                return name ? (
                                                                    <span key={pi} style={{
                                                                        background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                                                                        padding: '2px 8px', borderRadius: '12px', fontSize: '12px'
                                                                    }}>
                                                                        {name}
                                                                    </span>
                                                                ) : null;
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                                {t.budget_range && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Budget Range:</div>
                                                        <div style={{ fontSize: '14px', color: '#22c55e', fontWeight: 600 }}>{t.budget_range}</div>
                                                    </div>
                                                )}

                                                {t.confidence_score && (
                                                    <div style={{ marginBottom: '12px' }}>
                                                        <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>Confidence:</div>
                                                        <span style={{
                                                            background: confColor(t.confidence_score), color: '#000',
                                                            fontWeight: 700, padding: '2px 8px', borderRadius: '4px', fontSize: '12px'
                                                        }}>
                                                            {(t.confidence_score * 100).toFixed(0)}%
                                                        </span>
                                                    </div>
                                                )}

                                                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                                    {t.source_link || t.source_url || t.intel_source_link ? (
                                                        <a href={t.source_link || t.source_url || t.intel_source_link} target="_blank" rel="noopener noreferrer"
                                                            style={{ padding: '8px 16px', fontSize: '13px', color: '#d4af37', textDecoration: 'none' }} className="btn-secondary">
                                                            🔗 View Source ↗
                                                        </a>
                                                    ) : null}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {/* Pagination */}
                        {tendersTotal > TENDER_LIMIT && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '16px' }}>
                                {Array.from({ length: Math.ceil(tendersTotal / TENDER_LIMIT) }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setTenderPage(i)}
                                        style={{
                                            padding: '4px 10px',
                                            background: tenderPage === i ? '#d4af37' : 'rgba(212,175,55,0.1)',
                                            color: tenderPage === i ? '#0a1929' : '#d4af37',
                                            border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px',
                                        }}
                                    >{i + 1}</button>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ===== Tab Buttons ===== */}
            <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid rgba(212,175,55,0.2)', paddingBottom: '8px' }}>
                {tabs.map((t, i) => (
                    <button
                        key={i}
                        onClick={() => setTab(i)}
                        style={{
                            padding: '8px 16px',
                            background: tab === i ? 'rgba(212,175,55,0.1)' : 'transparent',
                            color: tab === i ? '#d4af37' : '#94a3b8',
                            border: 'none', borderRadius: '4px 4px 0 0', cursor: 'pointer', fontSize: '13px',
                        }}
                    >
                        {i === 0 ? '🔝 ' : i === 1 ? '🏢 ' : i === 2 ? '⚠️ ' : '📡 '}{t}
                    </button>
                ))}
            </div>

            {/* Patterns Tab */}
            {tab === 0 && (
                <div className="card" style={{ background: '#142238', overflowX: 'auto' }}>
                    {patterns.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No patterns yet. Run a scan to discover patterns.</div>
                    ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr>
                                    {['Pattern', 'Use Case', 'Products', 'Evidence', 'Confidence'].map((h, i) => (
                                        <th key={i} style={{ color: '#d4af37', padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid rgba(212,175,55,0.15)' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {patterns.map((p, i) => {
                                    const req = Array.isArray(p.required_products) ? p.required_products : [];
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                            <td style={{ padding: '8px', color: '#fff', fontWeight: 600 }}>{p.pattern_name}</td>
                                            <td style={{ padding: '8px', color: '#94a3b8' }}>{(p.use_case || '').slice(0, 80)}...</td>
                                            <td style={{ padding: '8px', color: '#94a3b8' }}>{req.slice(0, 3).join(', ')}</td>
                                            <td style={{ padding: '8px', color: '#94a3b8' }}>{p.evidence_count}</td>
                                            <td style={{ padding: '8px' }}>
                                                <span style={{ background: confColor(p.confidence_score), color: '#000', fontWeight: 700, padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                                                    {(p.confidence_score * 100).toFixed(0)}%
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Vendors Tab */}
            {tab === 1 && (
                <div className="card" style={{ background: '#142238', overflowX: 'auto' }}>
                    {vendors.length === 0 ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No vendors found yet.</div>
                    ) : (
                        <>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        {['Vendor', 'Country', 'Categories', 'Wins', 'Mentions', 'KB'].map((h, i) => (
                                            <th key={i} style={{ color: '#d4af37', padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid rgba(212,175,55,0.15)' }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {vendors.slice(vendorPage * 10, vendorPage * 10 + 10).map((v, i) => {
                                        const cats = Array.isArray(v.solution_categories) ? v.solution_categories : [];
                                        return (
                                            <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                <td style={{ padding: '8px', color: '#fff', fontWeight: 600 }}>{v.vendor_name}</td>
                                                <td style={{ padding: '8px', color: '#94a3b8' }}>{v.country}</td>
                                                <td style={{ padding: '8px', color: '#94a3b8' }}>{cats.slice(0, 2).join(', ')}</td>
                                                <td style={{ padding: '8px', color: '#22c55e' }}>{v.win_count}</td>
                                                <td style={{ padding: '8px', color: '#94a3b8' }}>{v.mention_count}</td>
                                                <td style={{ padding: '8px' }}>
                                                    {v.kb_entry_id
                                                        ? <span style={{ color: '#22c55e', fontSize: '12px' }}>✅ Linked</span>
                                                        : <span style={{ color: '#64748b', fontSize: '12px' }}>—</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '12px', fontSize: '13px' }}>
                                {Array.from({ length: Math.ceil(vendors.length / 10) }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setVendorPage(i)}
                                        style={{
                                            padding: '4px 10px',
                                            background: vendorPage === i ? '#d4af37' : 'rgba(212,175,55,0.1)',
                                            color: vendorPage === i ? '#0a1929' : '#d4af37',
                                            border: 'none', borderRadius: '4px', cursor: 'pointer',
                                        }}
                                    >{i + 1}</button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Gaps Tab */}
            {tab === 2 && (
                <div style={{ display: 'grid', gap: '12px' }}>
                    {gaps.length === 0 ? (
                        <div className="card" style={{ background: '#142238', padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No gaps detected. Great coverage! ✅</div>
                    ) : gaps.map((g, i) => (
                        <div key={i} className="card" style={{ background: '#142238', padding: '16px', borderLeft: '3px solid #ef4444' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <span style={{ color: '#ef4444' }}>⚠️</span>
                                <span style={{ color: '#fff', fontWeight: 600 }}>{g.solution_category}</span>
                                <span style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>{g.tender_count} tenders</span>
                            </div>
                            <div style={{ color: '#94a3b8', fontSize: '13px' }}>{g.recommendation}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Sources Tab */}
            {tab === 3 && (
                <div style={{ display: 'grid', gap: '12px' }}>
                    {sources.map((s) => (
                        <div key={s.id} className="card" style={{ background: '#142238', padding: '16px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                <div>
                                    <div style={{ color: '#fff', fontWeight: 600, fontSize: '16px' }}>{s.name}</div>
                                    <div style={{ color: '#94a3b8', fontSize: '13px', marginTop: '4px' }}>
                                        {s.country} • {s.base_url}
                                    </div>
                                    <div style={{ color: '#94a3b8', fontSize: '12px', marginTop: '8px' }}>
                                        Scraped: {s.total_tenders_scraped || 0}
                                        {s.last_scraped_at ? ` • Last: ${new Date(s.last_scraped_at).toLocaleDateString()}` : ' • Never'}
                                        {s.consecutive_failures > 0 ? ` • ⚠️ ${s.consecutive_failures} failures` : ''}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <span style={{
                                        background: s.is_active ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                                        color: s.is_active ? '#22c55e' : '#ef4444',
                                        padding: '4px 10px', borderRadius: '4px', fontSize: '12px',
                                    }}>
                                        {s.is_active ? '✅ Active' : '❌ Disabled'}
                                    </span>
                                    <button onClick={() => toggleSource(s.id, s.is_active)} style={{ padding: '4px 12px', fontSize: '12px' }} className="btn-secondary">
                                        {s.is_active ? 'Disable' : 'Enable'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}